import React from 'react';
import {
  Clock, HeartPulse, History, IndianRupee, Phone, Stethoscope,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDateIST } from '@/utils/date';
import QueuePriorityBadge from '@/components/QueuePriorityBadge';
import { formatDurationMinutes } from './formatVisitDuration';

const PAY_TONE = {
  paid: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  waived: 'bg-sky-50 text-sky-800 border-sky-200',
  unpaid: 'bg-amber-50 text-amber-900 border-amber-200',
};

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export default function ReceptionAppointmentCard({
  a, brand = '#F15A22', onCollect, onTriage, onHistory, collecting,
}) {
  const pay = a.payment_status || 'unpaid';
  const last = a.last_visit;
  const canHistory = Boolean(a.patient_id);

  return (
    <article
      className={`group relative overflow-hidden rounded-2xl border bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
        collecting ? 'ring-2 ring-amber-300' : 'border-[#D5DEE8]'
      }`}
      data-testid={`appt-card-${a.id}`}
    >
      <div className="absolute inset-x-0 top-0 h-1" style={{ background: brand }} />
      <div className="flex items-start gap-3">
        <div
          className="h-12 w-12 rounded-2xl flex items-center justify-center text-sm font-extrabold text-white shrink-0 shadow"
          style={{ background: `linear-gradient(145deg, ${brand}, #9a3412)` }}
        >
          {a.queue_no || '·'}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-heading text-lg text-[#071B34] truncate">{a.patient_name}</h3>
            <QueuePriorityBadge entry={a} />
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-900">
              {a.status_label || a.status || '—'}
            </span>
            <span className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded-full border ${PAY_TONE[pay] || PAY_TONE.unpaid}`}>
              {pay}
            </span>
          </div>
          <CardMeta a={a} />
          <LastVisitLine last={last} />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2 border-t border-[#EEF2F7] pt-3">
        {canHistory ? (
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={onHistory}
            data-testid={`appt-history-${a.id}`}>
            <History className="h-3.5 w-3.5" /> History
          </Button>
        ) : null}
        {a.kind === 'queue' && a.needs_triage ? (
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={onTriage}
            data-testid={`appt-triage-${a.id}`}>
            <HeartPulse className="h-3.5 w-3.5" /> Triage
          </Button>
        ) : null}
        {a.kind === 'queue' && a.needs_payment ? (
          <Button size="sm" className="h-8 text-xs text-white" style={{ background: brand }} onClick={onCollect}
            data-testid={`appt-collect-${a.id}`}>
            Collect payment
          </Button>
        ) : null}
      </div>
    </article>
  );
}

function CardMeta({ a }) {
  return (
    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-[12px] text-[#5A6B7C]">
      {a.patient_number ? (
        <span className="inline-flex items-center gap-1.5 min-w-0" title="Patient number">
          <span className="text-[9px] uppercase tracking-wider font-bold text-[#F15A22] shrink-0">Patient</span>
          <span className="font-mono text-[#071B34] font-semibold truncate">{a.patient_number}</span>
        </span>
      ) : null}
      {a.appointment_number ? (
        <span className="inline-flex items-center gap-1.5 min-w-0" title="Appointment number">
          <span className="text-[9px] uppercase tracking-wider font-bold text-[#0C2744] shrink-0">Appt</span>
          <span className="font-mono text-[#071B34] font-semibold truncate">{a.appointment_number}</span>
        </span>
      ) : null}
      <span className="inline-flex items-center gap-1.5">
        <Stethoscope className="h-3.5 w-3.5" /> {a.doctor_name}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Clock className="h-3.5 w-3.5" />
        {a.appointment_date ? formatDateIST(a.appointment_date) : '—'}
        {a.slot ? ` · ${a.slot}` : ''}
      </span>
      {a.patient_phone ? (
        <span className="inline-flex items-center gap-1.5">
          <Phone className="h-3.5 w-3.5" /> {a.patient_phone}
        </span>
      ) : null}
      <span className="inline-flex items-center gap-1.5">
        <IndianRupee className="h-3.5 w-3.5" /> {fmt(a.suggested_fee)}
      </span>
    </div>
  );
}

function LastVisitLine({ last }) {
  if (!last) {
    return (
      <p className="mt-2 text-[11px] text-[#9AADBE] italic" data-testid="last-visit-none">
        No prior visit on record
      </p>
    );
  }
  const total = formatDurationMinutes(last.total_hospital_minutes);
  return (
    <p className="mt-2 text-[11px] text-[#5A6B7C] rounded-lg bg-[#F8FAFC] border border-[#E8EEF5] px-2.5 py-1.5"
      data-testid="last-visit-line">
      <span className="text-[9px] uppercase tracking-wider font-bold text-[#F15A22] mr-1.5">Last visit</span>
      {last.date ? formatDateIST(last.date) : '—'}
      {last.time ? ` · ${last.time}` : ''}
      {' · '}
      <span className="font-semibold text-[#071B34]">{last.doctor_name || '—'}</span>
      {total ? (
        <span className="ml-1.5 text-[#071B34]">
          · {total} in hospital{last.total_hospital_open ? ' …' : ''}
        </span>
      ) : null}
    </p>
  );
}
